import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import PageTitle from '../../components/PageTitle';
import { tagService } from '../../services/api';
import { toast } from 'react-toastify';
import { Check, ChevronLeft, Tag as TagIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';

const TagForm = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6', // Default blue color
    isActive: true
  });
  
  const [loading, setLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const fetchTagDetails = useCallback(async () => {
    try {
      setLoading(true);
      // TODO: Implementar método para buscar tag ID no backend (Eu vou fazer isso, prometo)
      const response = await tagService.getTags({
        includePostCount: false,
        activeOnly: false
      });

      if (response.success) {
        const tag = response.data.tags.find(t => t.id === parseInt(id));
        if (tag) {
          setFormData({
            name: tag.name || '',
            description: tag.description || '',
            color: tag.color || '#3B82F6',
            isActive: tag.isActive
          });
        } else {
          toast.error('Tag não encontrada');
          navigate('/admin/tags');
        }
      } else {
        toast.error('Erro ao carregar detalhes da tag');
        navigate('/admin/tags');
      }
    } catch (error) {
      console.error('Erro ao buscar detalhes da tag:', error);
      toast.error('Falha ao carregar tag. Tente novamente mais tarde.');
      navigate('/admin/tags');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (isEditMode) {
      fetchTagDetails();
    }
  }, [isEditMode, fetchTagDetails]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Limpar erro quando o campo é alterado
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome da tag é obrigatório';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Nome da tag deve ter no mínimo 2 caracteres';
    } else if (formData.name.length > 50) {
      newErrors.name = 'Nome da tag deve ter no máximo 50 caracteres';
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Descrição deve ter no máximo 500 caracteres';
    }

    if (!formData.color || !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(formData.color)) {
      newErrors.color = 'Cor inválida';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        color: formData.color,
        isActive: formData.isActive
      };

      let response;
      
      if (isEditMode) {
        response = await tagService.updateTag(id, payload);
      } else {
        response = await tagService.createTag(payload);
      }

      if (response.success) {
        toast.success(isEditMode ? 'Tag atualizada com sucesso' : 'Tag criada com sucesso');
        navigate('/admin/tags');
      } else {
        toast.error(`Erro: ${response.message}`);
      }
    } catch (error) {
      console.error('Erro ao salvar tag:', error);
      toast.error(`Falha ao ${isEditMode ? 'atualizar' : 'criar'} tag. Tente novamente mais tarde.`);
    } finally {
      setSubmitting(false);
    }
  };

  // Lista de cores predefinidas
  const predefinedColors = [
    '#3B82F6', // Blue
    '#EF4444', // Red
    '#10B981', // Green
    '#F59E0B', // Yellow/Orange
    '#8B5CF6', // Purple
    '#06B6D4', // Cyan
    '#84CC16', // Lime
    '#EC4899', // Pink
    '#6B7280', // Gray
    '#000000', // Black
  ];

  if (loading) {
    return (
      <div className="container mx-auto p-4 flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-church-blue"></div>
      </div>
    );
  }

  return (
    <>
      <PageTitle 
        title={isEditMode ? 'Editar Tag' : 'Criar Nova Tag'} 
        description={isEditMode ? `Editando tag: ${formData.name}` : 'Crie uma nova tag para organizar os estudos'}
      />
      <div className="container mx-auto p-6 max-w-3xl">
      <div className="flex flex-col space-y-8">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" asChild className="h-8 w-8 p-0">
            <Link to="/admin/tags">
              <ChevronLeft className="h-4 w-4" />
            </Link>
          </Button>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEditMode ? 'Editar Tag' : 'Nova Tag'}
          </h1>
        </div>

        <Card>
          <form onSubmit={handleSubmit}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TagIcon className="h-5 w-5" />
                Informações da Tag
              </CardTitle>
              <CardDescription>
                Preencha os dados abaixo para {isEditMode ? 'editar' : 'criar'} uma tag
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Nome */}
              <div className="space-y-2">
                <Label htmlFor="name">
                  Nome<span className="text-destructive ml-1">*</span>
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Nome da tag"
                  className={errors.name ? 'border-destructive' : ''}
                  disabled={submitting}
                  maxLength={50}
                  required
                />
                {errors.name && (
                  <p className="text-destructive text-sm">{errors.name}</p>
                )}
              </div>
      
              {/* Descrição */}
              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Descreva o propósito desta tag (opcional)"
                  rows={3}
                  className={errors.description ? 'border-destructive' : ''}
                  disabled={submitting}
                />
                {errors.description && (
                  <p className="text-destructive text-sm">{errors.description}</p>
                )}
                <p className="text-muted-foreground text-xs">
                  {formData.description.length}/500 caracteres
                </p>
              </div>
      
              {/* Cor */}
              <div className="space-y-3">
                <Label htmlFor="color">Cor</Label>
                <div className="flex items-center gap-3">
                  <div className="relative border rounded-md p-1 overflow-hidden">
                    <input
                      id="color"
                      name="color"
                      type="color"
                      value={formData.color}
                      onChange={handleChange}
                      className="w-10 h-10 border-0 p-0 m-0"
                      disabled={submitting}
                    />
                  </div>
                  <Input
                    type="text"
                    name="color"
                    value={formData.color}
                    onChange={handleChange}
                    className={`max-w-[120px] ${errors.color ? 'border-destructive' : ''}`}
                    placeholder="#000000"
                    disabled={submitting}
                  />
                </div>
                {errors.color && (
                  <p className="text-destructive text-sm">{errors.color}</p>
                )}
      
                <div className="flex flex-wrap gap-2">
                  {predefinedColors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, color }))}
                      className="w-8 h-8 rounded-full border border-border flex items-center justify-center hover:scale-110 transition-transform"
                      style={{ backgroundColor: color }}
                      title={color}
                    >
                      {formData.color === color && (
                        <Check className="h-4 w-4 text-white" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
      
              {/* Status */}
              <div className="flex items-center justify-between pt-2">
                <div className="space-y-0.5">
                  <Label htmlFor="isActive">Tag ativa</Label>
                  <p className="text-sm text-muted-foreground">
                    Tags inativas não aparecem em pesquisas ou seletores de tags
                  </p>
                </div>
                <Switch
                  id="isActive"
                  name="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => 
                    setFormData(prev => ({ ...prev, isActive: checked }))
                  }
                  disabled={submitting}
                />
              </div>
            </CardContent>
            <CardFooter className="flex justify-between border-t pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin/tags')}
                disabled={submitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="igreja"
                disabled={submitting}
                className="gap-2"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white"></div>
                    Salvando...
                  </>
                ) : (
                  <>Salvar</>
                )}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
    </>
  );
};

export default TagForm;
